import { useCallback, useEffect, useRef } from 'react';
import { useBroadcastChannel, type BroadcastMessage } from './useBroadcastChannel';

/**
 * Chaves do localStorage para sincronização entre abas
 */
const STORAGE_KEYS = {
  LAST_ACTIVITY: 'd12-last-activity',
  LAST_SEEN_UPDATE: 'd12-last-seen-update',
  ACTIVE_TABS: 'd12-active-tabs',
  DATE_RANGE: 'd12-date-range',
} as const;

/**
 * Interface para estado de atividade compartilhado
 */
interface SharedActivityState {
  timestamp: number;
  tabId: string;
}

/**
 * Interface para registro de abas ativas
 */
interface ActiveTabsRegistry {
  [tabId: string]: {
    lastSeen: number;
    isVisible: boolean;
  };
}

/**
 * Hook para sincronização de estado entre abas
 *
 * Fornece:
 * - Rastreamento de atividade compartilhada entre abas
 * - Registro de abas ativas
 * - Sincronização de timestamps de última atualização
 */
export function useCrossTabSync() {
  const { postMessage, tabId } = useBroadcastChannel();
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Atualiza o timestamp de última atividade (compartilhado entre abas)
   */
  const updateSharedActivity = useCallback(() => {
    const state: SharedActivityState = {
      timestamp: Date.now(),
      tabId,
    };

    try {
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, JSON.stringify(state));
    } catch (error) {
      console.warn('[CrossTabSync] Error updating shared activity:', error);
    }

    // Notificar outras abas
    postMessage('ACTIVITY_UPDATE', state);
  }, [postMessage, tabId]);

  /**
   * Obtém o timestamp de última atividade (de qualquer aba)
   */
  const getLastActivity = useCallback((): number => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
      if (stored) {
        const state = JSON.parse(stored) as SharedActivityState;
        return state.timestamp;
      }
    } catch (error) {
      console.warn('[CrossTabSync] Error reading last activity:', error);
    }
    return 0;
  }, []);

  /**
   * Verifica se houve atividade recente em qualquer aba
   */
  const hasRecentActivity = useCallback((withinMs: number): boolean => {
    const lastActivity = getLastActivity();
    return Date.now() - lastActivity < withinMs;
  }, [getLastActivity]);

  /**
   * Registra esta aba como ativa
   */
  const registerTab = useCallback((isVisible: boolean) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_TABS);
      const registry: ActiveTabsRegistry = stored ? JSON.parse(stored) : {};

      registry[tabId] = {
        lastSeen: Date.now(),
        isVisible,
      };

      // Limpar abas inativas (não vistas há mais de 1 minuto)
      const now = Date.now();
      for (const [id, data] of Object.entries(registry)) {
        if (now - data.lastSeen > 60000) {
          delete registry[id];
        }
      }

      localStorage.setItem(STORAGE_KEYS.ACTIVE_TABS, JSON.stringify(registry));
    } catch (error) {
      console.warn('[CrossTabSync] Error registering tab:', error);
    }
  }, [tabId]);

  /**
   * Remove esta aba do registro
   */
  const unregisterTab = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_TABS);
      if (stored) {
        const registry: ActiveTabsRegistry = JSON.parse(stored);
        delete registry[tabId];
        localStorage.setItem(STORAGE_KEYS.ACTIVE_TABS, JSON.stringify(registry));
      }
    } catch (error) {
      console.warn('[CrossTabSync] Error unregistering tab:', error);
    }
  }, [tabId]);

  /**
   * Obtém o número de abas ativas
   */
  const getActiveTabCount = useCallback((): number => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_TABS);
      if (stored) {
        const registry: ActiveTabsRegistry = JSON.parse(stored);
        const now = Date.now();

        // Contar apenas abas ativas nos últimos 30 segundos
        return Object.values(registry).filter(
          data => now - data.lastSeen < 30000
        ).length;
      }
    } catch (error) {
      console.warn('[CrossTabSync] Error counting active tabs:', error);
    }
    return 1; // Pelo menos esta aba
  }, []);

  /**
   * Verifica se há outras abas visíveis
   */
  const hasOtherVisibleTabs = useCallback((): boolean => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_TABS);
      if (stored) {
        const registry: ActiveTabsRegistry = JSON.parse(stored);
        const now = Date.now();

        // Verificar se há outras abas visíveis (exceto esta)
        return Object.entries(registry).some(
          ([id, data]) =>
            id !== tabId &&
            data.isVisible &&
            now - data.lastSeen < 30000
        );
      }
    } catch (error) {
      console.warn('[CrossTabSync] Error checking visible tabs:', error);
    }
    return false;
  }, [tabId]);

  /**
   * Atualiza timestamp de última atualização do lastSeen no banco
   */
  const updateLastSeenTimestamp = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_SEEN_UPDATE, Date.now().toString());
    } catch (error) {
      console.warn('[CrossTabSync] Error updating lastSeen timestamp:', error);
    }

    // Notificar outras abas
    postMessage('LAST_SEEN_UPDATED');
  }, [postMessage]);

  /**
   * Verifica se já houve atualização recente do lastSeen (de qualquer aba)
   */
  const hasRecentLastSeenUpdate = useCallback((withinMs: number): boolean => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.LAST_SEEN_UPDATE);
      if (stored) {
        const timestamp = parseInt(stored, 10);
        return Date.now() - timestamp < withinMs;
      }
    } catch (error) {
      console.warn('[CrossTabSync] Error reading lastSeen timestamp:', error);
    }
    return false;
  }, []);

  /**
   * Salva date range no localStorage para sincronização
   */
  const saveDateRange = useCallback((startDate: string, endDate: string) => {
    try {
      localStorage.setItem(STORAGE_KEYS.DATE_RANGE, JSON.stringify({ startDate, endDate }));
    } catch (error) {
      console.warn('[CrossTabSync] Error saving date range:', error);
    }

    // Notificar outras abas
    postMessage('DATE_RANGE_CHANGED', { startDate, endDate });
  }, [postMessage]);

  /**
   * Carrega date range do localStorage
   */
  const loadDateRange = useCallback((): { startDate: string; endDate: string } | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.DATE_RANGE);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('[CrossTabSync] Error loading date range:', error);
    }
    return null;
  }, []);

  // Registrar aba e configurar heartbeat
  useEffect(() => {
    // Registrar esta aba
    registerTab(!document.hidden);

    // Atualizar visibilidade quando mudar
    const handleVisibilityChange = () => {
      registerTab(!document.hidden);

      if (!document.hidden) {
        postMessage('TAB_VISIBLE');
      } else {
        postMessage('TAB_HIDDEN');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Heartbeat para manter registro atualizado
    heartbeatRef.current = setInterval(() => {
      registerTab(!document.hidden);
    }, 10000); // A cada 10 segundos

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }

      unregisterTab();
    };
  }, [registerTab, unregisterTab, postMessage]);

  // Cleanup ao fechar aba
  useEffect(() => {
    const handleBeforeUnload = () => {
      unregisterTab();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unregisterTab]);

  return {
    tabId,
    postMessage,
    updateSharedActivity,
    getLastActivity,
    hasRecentActivity,
    getActiveTabCount,
    hasOtherVisibleTabs,
    updateLastSeenTimestamp,
    hasRecentLastSeenUpdate,
    saveDateRange,
    loadDateRange,
  };
}

/**
 * Hook para escutar mensagens de outras abas
 */
export function useCrossTabListener(
  onMessage: (message: BroadcastMessage) => void
) {
  const { postMessage, tabId } = useBroadcastChannel(onMessage);

  return { postMessage, tabId };
}
